import React, { FunctionComponent, useEffect, useState } from "react"
import BaseButton from "../components/Button/base-button"
import { Button, Modal } from "react-daisyui"
import LoginInput from "../features/auth/components/login-input"
import { RootState, useTypedSelector } from "../app/store"
import {
  PostUserapiUsersDataCreateGroupApiResponse,
  useCreateGroupMutation,
  useDeleteGroupMutation,
  useGetGroupsByOrganizationIdQuery,
  useUpdateGroupMutation,
} from "../app/services/meseeknaApi"
import { FaCircleNotch, FaEdit, FaTrash } from "react-icons/fa"
import NotificationContainer from "../features/auth/components/Notifications/NotificationContainer"
import { useNavigate } from "react-router-dom"

type Group = {
  id: number
  name: string
  organizationId: number
  createdBy: number
  modifiedBy: string | number | null
  createdOn: string
  status: number
}

const GroupsList: FunctionComponent = () => {
  const authStore = useTypedSelector((state: RootState) => {
    return state.authSlice
  })
  const navigate = useNavigate()
  const [createGroup, setCreateGroup] = useState<boolean>(false)
  const [editGroup, setEditGroup] = useState<boolean>(false)
  const [editGroupId, setEditGroupId] = useState<number | null>(null)
  // const [openDialogue, setOpenDialogue] = useState<boolean>
  const [inputError, setInputError] = useState<string>("")
  const [groupName, setGroupName] = useState<string>("")
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [groupsList, setGroupsList] = useState<Group[]>([])
  const [deleteGroup, setDeleteGroup] = useState<number | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [successMessage, setSuccessMessage] = useState<string>("")
  const {
    data: groupsListResponse,
    isFetching,
    refetch: refetchGroupsList,
  } = useGetGroupsByOrganizationIdQuery({
    organizationId: Number(authStore.organization?.id),
  })
  const [createGroupApi] = useCreateGroupMutation()
  const [updateGroupApi] = useUpdateGroupMutation()
  const [deleteGroupApi] = useDeleteGroupMutation()

  // Icon wrapper components
  // @ts-expect-error - React icons return ReactNode which is valid for JSX
  const LoadingSpinner = (): JSX.Element => <FaCircleNotch className="animate-spin text-blue-500 m-auto" size={24} />
  // @ts-expect-error - React icons return ReactNode which is valid for JSX
  const EditIcon = (): JSX.Element => <FaEdit className="w-5 h-5" />
  // @ts-expect-error - React icons return ReactNode which is valid for JSX
  const TrashIcon = (): JSX.Element => <FaTrash className="w-4 h-5" />

  const handleViewUsers = (group: Group) => {
    navigate(`/dashboard/recruiter-admin/users/${group.id}`, {
      state: { group: group },
    })
  }

  const handleEdit = (group: Group) => {
    setGroupName(group.name)
    setEditGroupId(group.id)
    setEditGroup(true)
  }

  const handleCancel = () => {
    setCreateGroup(false)
    setEditGroup(false)
    setDeleteGroup(null)
    setEditGroupId(null)
    setGroupName("")
    setInputError("")
    refetchGroupsList()
  }

  const handleSubmit = async () => {
    if (!groupName || !groupName.trim()) {
      setInputError("Group name is required")
      return
    }

    setErrorMessage("")
    setSuccessMessage("")
    setIsLoading(true)

    try {
      let response

      if (createGroup) {
        const payload = {
          name: groupName,
          organizationId: Number(authStore.organization.id),
          createdBy: Number(authStore.user.id),
        }
        response = await createGroupApi({
          body: payload,
        })
      } else {
        const payload = {
          name: groupName,
          groupId: Number(editGroupId),
          organizationId: Number(authStore.organization.id),
          modifiedBy: Number(authStore.user.id),
        }
        response = await updateGroupApi({
          body: payload,
        })
      }
      if ("error" in response) {
        if ("data" in response.error) {
          const responseData = response.error
            .data as PostUserapiUsersDataCreateGroupApiResponse
          setErrorMessage(responseData.message ? responseData.message : "")
        }
      } else if ("data" in response && response.data.status == "success") {
        setSuccessMessage(
          createGroup
            ? "Group created successfully"
            : "Group updated successfully",
        )
      }
      handleCancel()
    } catch (error) {
      console.error("Group create or update error: ", error)
      setErrorMessage("Something went wrong")
    }
    setIsLoading(false)
  }

  const handleDelete = async () => {
    setErrorMessage("")
    setSuccessMessage("")
    setIsLoading(true)
    try {
      const response = await deleteGroupApi({
        groupId: Number(deleteGroup),
      })
      if ("data" in response && response.data.status === "success") {
        setSuccessMessage("Group deleted successfully")
      }
    } catch (error) {
      console.error("Error occured while deleting the Group: ", error)
      setErrorMessage("Something went wrong")
    }
    setIsLoading(false)
    handleCancel()
  }

  useEffect(() => {
    if (
      groupsListResponse &&
      groupsListResponse.status === "success" &&
      "data" in groupsListResponse &&
      groupsListResponse.data &&
      groupsListResponse.data.length > 0
    ) {
      setGroupsList(groupsListResponse.data)
    }
  }, [groupsListResponse])

  return (
    <React.Fragment>
      <NotificationContainer
        errorMessage={errorMessage}
        successMessage={successMessage}
      />
      <div className="py-8 px-8 flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-semibold text-gray-900">Groups</h1>
            <BaseButton
              variant="primary"
              onClick={() => setCreateGroup(true)}
              className="w-fit"
              type="submit">
              Create Group
            </BaseButton>
          </div>
          <p className="text-gray-600">
            Organize your people into teams, departments, or cohorts. Groups make it easier to deploy simulations, track progress, and compare cognitive patterns across your organization.
          </p>
        </div>
        {isFetching ? (
          <div data-testid="loading-spinner" className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : groupsList.length > 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-100">
            {groupsList.map((group: Group) => {
              return (
                <div
                  className="flex justify-between items-center px-6 py-4 hover:bg-gray-50 transition-colors"
                  key={group.id}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                      <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <span className="font-medium text-gray-900">{group.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid="view-users-icon"
                      className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      onClick={() => handleViewUsers(group)}
                      title="View Users"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <button
                      data-testid="edit-icon"
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      onClick={() => handleEdit(group)}
                      title="Edit Group"
                    >
                      <EditIcon />
                    </button>
                    <button
                      data-testid="delete-icon"
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      onClick={() => setDeleteGroup(group.id)}
                      title="Delete Group"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <p className="text-gray-500">No groups created yet.</p>
            <p className="text-gray-400 text-sm mt-1">Click &quot;Create Group&quot; above to organize your team.</p>
          </div>
        )}
        <Modal open={createGroup || editGroup || deleteGroup !== null}>
          <Modal.Header className="flex justify-between items-center">
            <span className="font-bold text-lg">
              {deleteGroup !== null ? "Delete" : createGroup ? "Add" : "Edit"}{" "}
              Group
            </span>
            <Button
              className="btn-sm text-gray-700 bg-base-white shadow-none border-[1px] border-solid border-gray-300 hover:text-gray-700 hover:bg-blue-light-200"
              shape="circle"
              onClick={handleCancel}>
              ✕
            </Button>
          </Modal.Header>
          <Modal.Body className="self-stretch flex flex-col items-start justify-start gap-[20px]">
            {deleteGroup === null ? (
              <LoginInput
                id="createOrEditGroup"
                inputTitleText="Group Name"
                inputPlaceholderText="Group Name"
                required={true}
                error={inputError}
                defaultValue={groupName}
                setValue={setGroupName}
              />
            ) : (
              <div>Are you sure you want to delete this group permanently?</div>
            )}
          </Modal.Body>
          <Modal.Actions>
            <BaseButton
              variant="primary"
              onClick={() => {
                deleteGroup !== null ? handleDelete() : handleSubmit()
              }}
              loading={isLoading}>
              {deleteGroup !== null ? "Delete" : "Submit"}
            </BaseButton>
            <BaseButton variant="secondary" onClick={handleCancel}>
              Cancel
            </BaseButton>
          </Modal.Actions>
        </Modal>
      </div>
    </React.Fragment>
  )
}

export default GroupsList
